/* eslint-disable jsx-a11y/anchor-is-valid */
import { AppstoreFilled, TableOutlined as TableIcon } from '@ant-design/icons';
import { AutoComplete, Button, Card, Col, Empty, Image, Input, Row, Skeleton, Space, Table, Tag } from 'antd';
import { Investment } from 'procura-shared';
import React, { useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { PLACEHOLDER_IMG } from '../utilities/Constants';
import { investmentsErrors } from '../utilities/Utilities';
import { NewInvestmentButton } from './Components';

type InvestmentsTableProps = {
  investments: Investment[];
  table?: boolean;
  search?: boolean;
  loading?: boolean;
  scroll?: boolean;
  errors?: boolean;
  adminMode?: boolean;
  changeUseTable?: (state: boolean) => void;
  onSelect?: (investment: Investment) => void;
};

export const InvestmentsTable = ({
  investments,
  table = true,
  search = false,
  loading = false,
  scroll = false,
  errors = false,
  adminMode = false,
  changeUseTable,
  onSelect,
}: InvestmentsTableProps) => {
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState('');
  const [tableState, setTableState] = useState(table);

  const isBigTable = () => (noData() ? false : investments.length > 12);
  const isLoading = () => loading;
  const noData = () => investments == null || investments.length === 0;

  const filteredInvestments = () => {
    if (noData()) return [];
    return (
      searchInput === ''
        ? investments
        : investments.filter((investment) => investment.name?.toLocaleLowerCase().includes(searchInput.toLowerCase()))
    )
      .sort()
      .map((e, i) => ({ ...e, key: i }));
  };

  const columns = () => {
    return [
      {
        title: t('name'),
        dataIndex: 'name',
        key: 'name',
        render: (text, investment) => (
          <a onClick={() => onSelect?.(investment)}>
            {searchInput !== '' ? (
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchInput]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
              />
            ) : (
              text
            )}
          </a>
        ),
      },
      {
        title: t('address'),
        dataIndex: 'address',
        key: 'address',
      },
      ...(errors
        ? [
            {
              title: t('errors'),
              key: 'errors',
              render: (text, investment) => renderErrors(investment),
            },
          ]
        : []),
    ];
  };

  const setTable = (state: boolean) => {
    changeUseTable?.(state);
    setTableState(state);
  };

  const renderSearch = () => (
    <AutoComplete
      className="pg-u-full-width"
      style={{ marginBottom: 16 }}
      options={investments?.map((investment) => ({ value: investment.name }))}
      filterOption={(inputValue, option) => option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
      onSelect={(e) => setSearchInput(e)}>
      <Input.Search
        className="pg-u-full-width"
        allowClear
        placeholder={t('search')}
        onSearch={(e) => setSearchInput(e)}
        onChange={(e) => {
          const value = (e.target as any).value;
          if (value === '') {
            setSearchInput('');
          }
        }}
      />
    </AutoComplete>
  );

  const renderErrors = (investment: Investment) =>
    investmentsErrors(investment, t).map((error) => <Tag color="warning">{error}</Tag>);

  const renderTable = () => (
    <>
      <Space direction="vertical" style={{ width: '100%' }}>
        {search && renderSearch()}
        {adminMode && <NewInvestmentButton />}
      </Space>
      <Table
        pagination={false}
        loading={isLoading()}
        bordered
        dataSource={filteredInvestments()}
        columns={columns()}
        scroll={{ y: 500 }}
        sticky
        size={isBigTable() ? 'small' : 'large'}
      />
    </>
  );

  const renderCards = () => (
    <>
      <Space direction="vertical" style={{ width: '100%' }}>
        {search && renderSearch()}
        {adminMode && (
          <Button style={{ width: '100%', marginBottom: 16 }} type="ghost">
            {t('newInvestment')}
          </Button>
        )}
      </Space>
      <Row gutter={[24, 24]} style={scroll && { overflowY: 'scroll', maxHeight: '800px' }}>
        {filteredInvestments().map((investment, index) => (
          <Col span={investments.length >= 2 ? 12 : 24} key={index}>
            <div className="pg-investment-card">
              <Card cover={<Image src={investment.imageURL ?? PLACEHOLDER_IMG} fallback={PLACEHOLDER_IMG} />}>
                <div onClick={(_) => onSelect?.(investment)}>
                  <Card.Meta
                    title={
                      <a>
                        <Highlighter
                          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                          searchWords={[searchInput]}
                          autoEscape
                          textToHighlight={investment.name ? investment.name.toString() : ''}
                        />
                      </a>
                    }
                    description={investment.address}
                  />
                  {errors && renderErrors(investment)}
                </div>
              </Card>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );

  const renderLoadingCards = () => (
    <Row gutter={[24, 24]} className={`${scroll ? 'pg-scroll-list' : ''}`}>
      {[...Array(2)].map((_, index) => (
        <Col span={12} key={index}>
          <div className="pg-investment-card">
            <Card cover={<Image src={PLACEHOLDER_IMG} />}>
              <Skeleton active />
            </Card>
          </div>
        </Col>
      ))}
    </Row>
  );

  return !isLoading() && noData() ? (
    <Empty />
  ) : (
    <>
      {tableState ? renderTable() : isLoading() ? renderLoadingCards() : renderCards()}
      <Space direction="horizontal" style={{ padding: 8 }}>
        <AppstoreFilled
          className={`pg-icon-m ${!tableState ? 'pg-color-selected' : ''}`}
          onClick={(_) => setTable(false)}
        />
        <TableIcon className={`pg-icon-m ${tableState ? 'pg-color-selected' : ''}`} onClick={(_) => setTable(true)} />
      </Space>
    </>
  );
};
